/**
 * Date utility functions for the UI package
 */

interface FormatOptions {
  locale?: string;
  addSuffix?: boolean;
}

/**
 * Format distance between two dates in a human-readable format
 * @param date - The date to format
 * @param baseDate - The date to compare against (default: now)
 * @param options - Formatting options
 */
export function formatDistanceToNow(date: Date, options: FormatOptions = {}): string {
  const { locale = 'en-US', addSuffix = true } = options;

  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  let distance: string;

  if (locale === 'tr-TR') {
    if (diffInSeconds < 60) {
      distance = 'az önce';
    } else if (diffInMinutes < 60) {
      distance = `${diffInMinutes} dakika${addSuffix ? ' önce' : ''}`;
    } else if (diffInHours < 24) {
      distance = `${diffInHours} saat${addSuffix ? ' önce' : ''}`;
    } else if (diffInDays < 7) {
      distance = `${diffInDays} gün${addSuffix ? ' önce' : ''}`;
    } else if (diffInWeeks < 4) {
      distance = `${diffInWeeks} hafta${addSuffix ? ' önce' : ''}`;
    } else if (diffInMonths < 12) {
      distance = `${diffInMonths} ay${addSuffix ? ' önce' : ''}`;
    } else {
      distance = `${diffInYears} yıl${addSuffix ? ' önce' : ''}`;
    }
  } else {
    // English fallback
    if (diffInSeconds < 60) {
      distance = 'just now';
    } else if (diffInMinutes < 60) {
      distance = `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''}${addSuffix ? ' ago' : ''}`;
    } else if (diffInHours < 24) {
      distance = `${diffInHours} hour${diffInHours > 1 ? 's' : ''}${addSuffix ? ' ago' : ''}`;
    } else if (diffInDays < 7) {
      distance = `${diffInDays} day${diffInDays > 1 ? 's' : ''}${addSuffix ? ' ago' : ''}`;
    } else if (diffInWeeks < 4) {
      distance = `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''}${addSuffix ? ' ago' : ''}`;
    } else if (diffInMonths < 12) {
      distance = `${diffInMonths} month${diffInMonths > 1 ? 's' : ''}${addSuffix ? ' ago' : ''}`;
    } else {
      distance = `${diffInYears} year${diffInYears > 1 ? 's' : ''}${addSuffix ? ' ago' : ''}`;
    }
  }

  return distance;
}

/**
 * Format a date in a localized format
 * @param date - The date to format
 * @param locale - The locale to use
 * @param options - Intl.DateTimeFormat options
 */
export function formatDate(
  date: Date,
  locale: string = 'tr-TR',
  options?: Intl.DateTimeFormatOptions
): string {
  return new Intl.DateTimeFormat(locale, options).format(date);
}

/**
 * Check if a date is today
 * @param date - The date to check
 */
export function isToday(date: Date): boolean {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

/**
 * Check if a date is yesterday
 * @param date - The date to check
 */
export function isYesterday(date: Date): boolean {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
}
